import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Article reader — renders the JSX Body of a single Help article with
 * consistent prose typography, plus an auto-generated "On this page" table of
 * contents built from the article's headings (no article body changes needed).
 *
 * If the slug is unknown OR the viewer isn't allowed to see it (persona audience
 * OR the article's optional feature-flag gate), we redirect to HelpHome.
 */
import { ArrowLeft, Calendar, Clock, List } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { PageBody, PageHeader } from '@/components/AppShell';
import { articleVisible } from '@/components/help/audience';
import { Card, CardBody } from '@/components/ui/Card';
import { findArticle } from '@/content/help/articles';
import { resolvePersona, useAuthStore } from '@/store/auth';
function slugify(text) {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}
export function HelpArticlePage() {
    const { slug } = useParams();
    const me = useAuthStore((s) => s.me);
    const persona = resolvePersona(me);
    const featureFlags = me?.feature_flags;
    const bodyRef = useRef(null);
    const [toc, setToc] = useState([]);
    const article = slug ? findArticle(slug) : undefined;
    // Build the "On this page" list from the rendered headings after the body
    // mounts. Assigns a stable id to each h2/h3 (so the in-page links work)
    // without the article authors having to add anchors by hand. Re-runs per slug.
    useEffect(() => {
        if (!article)
            return;
        const root = bodyRef.current;
        if (!root)
            return;
        const heads = Array.from(root.querySelectorAll('h2, h3'));
        const seen = new Set();
        const entries = heads.map((h) => {
            const text = h.textContent?.trim() ?? '';
            let id = h.id || slugify(text) || 'section';
            // De-dupe ids so two identically-named headings still anchor distinctly.
            let n = 2;
            const base = id;
            while (seen.has(id))
                id = `${base}-${n++}`;
            seen.add(id);
            h.id = id;
            // Offset for the sticky page header so anchor jumps don't hide the title.
            h.style.scrollMarginTop = '5rem';
            return { id, text, level: h.tagName === 'H3' ? 3 : 2 };
        });
        setToc(entries);
    }, [article, slug]);
    if (!article)
        return _jsx(Navigate, { to: "/help", replace: true });
    // Defence-in-depth: a deep link to an article not for this viewer (wrong role
    // OR a feature the tenant hasn't enabled) goes home rather than rendering
    // content they shouldn't see.
    if (!articleVisible(article, persona, featureFlags))
        return _jsx(Navigate, { to: "/help", replace: true });
    const { Body } = article;
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: article.title, description: article.excerpt, actions: _jsxs(Link, { to: "/help", className: "inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800", children: [_jsx(ArrowLeft, { size: 14 }), "All articles"] }) }), _jsx(PageBody, { children: _jsxs("div", { className: "mx-auto flex max-w-5xl gap-6", children: [_jsx("div", { className: "min-w-0 max-w-3xl flex-1", children: _jsx(Card, { children: _jsxs(CardBody, { children: [_jsxs("div", { className: "mb-4 flex items-center gap-4 text-[11px] text-slate-400", children: [_jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx(Calendar, { size: 11 }), "Updated ", article.updatedAt] }), article.readMinutes && (_jsxs("span", { className: "inline-flex items-center gap-1", children: [_jsx(Clock, { size: 11 }), article.readMinutes, " min read"] }))] }), _jsx("article", { ref: bodyRef, className: "prose-help", children: _jsx(Body, {}) })] }) }) }), toc.length >= 2 && (_jsx("aside", { className: "hidden w-56 shrink-0 lg:block", children: _jsxs("div", { className: "sticky top-20", children: [_jsxs("div", { className: "mb-2 inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400", children: [_jsx(List, { size: 12 }), "On this page"] }), _jsx("nav", { className: "space-y-1 border-l border-slate-200 dark:border-slate-800", children: toc.map((t) => (_jsx("a", { href: `#${t.id}`, className: 'block border-l-2 border-transparent py-0.5 text-xs text-slate-500 hover:border-brand-400 hover:text-brand-700 dark:text-slate-400 dark:hover:text-brand-300 ' +
                                                (t.level === 3 ? 'pl-5' : 'pl-3 font-medium'), children: t.text }, t.id))) })] }) }))] }) })] }));
}
